import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Vote, Copy, Plus, Trash2 } from 'lucide-react';
import {
  getMyDonatePoll,
  saveDonatePoll,
  DonatePoll,
} from '../../../api/payment/donate-poll.api';

const MAX_OPTIONS = 4;
const formatCa = (v: number) => Number(v || 0).toLocaleString('vi-VN');

/**
 * Cấu hình "Bình chọn bằng donate" cho streamer.
 * Hết giờ, phương án nhiều Ca nhất thắng và streamer làm theo.
 * Ca của phương án thua vẫn thuộc về streamer (không hoàn) — nhắc rõ trên giao diện.
 */
const DonatePollSetting: React.FC<{ userId?: string }> = ({ userId }) => {
  const { host, protocol } = window.location;
  const overlayUrl = `${protocol}//${host}/donate-poll-screen/${userId}`;

  const [poll, setPoll] = useState<DonatePoll | null>(null);
  const [question, setQuestion] = useState('');
  const [duration, setDuration] = useState<string>('15');
  const [isActive, setIsActive] = useState(false);
  const [startNewRound, setStartNewRound] = useState(false);
  const [options, setOptions] = useState<string[]>(['', '']);
  const [saving, setSaving] = useState(false);

  const apply = (p: DonatePoll) => {
    setPoll(p);
    if (p?.exists) {
      setQuestion(p.question || '');
      setIsActive(!!p.isActive);
      if (p.options?.length) setOptions(p.options.map((o) => o.label));
    }
  };

  useEffect(() => {
    getMyDonatePoll()
      .then((p) => {
        if (p?.exists) apply(p);
      })
      .catch(() => {
        /* chưa tạo lần nào -> form mặc định */
      });
  }, []);

  const handleSave = () => {
    if (saving) return;
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (isActive && !question.trim()) {
      toast.error('Vui lòng nhập câu hỏi bình chọn.');
      return;
    }
    if (isActive && opts.length < 2) {
      toast.error('Cần ít nhất 2 phương án.');
      return;
    }
    setSaving(true);
    saveDonatePoll({
      question: question.trim(),
      durationMinutes: Number(duration) || 15,
      isActive,
      options: opts,
      startNewRound,
    })
      .then((p) => {
        apply(p);
        setStartNewRound(false);
        toast.success('Đã lưu bình chọn');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Lưu bình chọn thất bại');
      })
      .finally(() => setSaving(false));
  };

  const copyOverlay = async () => {
    try {
      await navigator.clipboard.writeText(overlayUrl);
      toast.success('Đã sao chép link overlay');
    } catch {
      toast.error('Trình duyệt chặn sao chép — hãy bôi đen link rồi copy tay.');
    }
  };

  const inputCls =
    'h-11 rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-base dark:text-white';

  return (
    <div className="border rounded-xl border-[#DEE0E6] dark:border-slate-700 mt-[10px]">
      <p className="p-3 flex items-center gap-2 w-full text-[#18191E] dark:text-white font-semibold text-[22px] leading-[27px]">
        <Vote size={20} className="text-blue-600" />
        Bình chọn bằng donate
      </p>

      <div className="w-full flex flex-col gap-3 bg-[#f6f7f8] dark:bg-dark2 p-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Đưa ra vài phương án, người xem donate cho phương án họ muốn. Hết giờ, phương án
          nhiều Ca nhất thắng và <b>bạn làm theo phương án đó</b>. Ca của phương án thua
          vẫn thuộc về bạn — người xem sẽ được báo rõ điều này trước khi bình chọn.
        </p>

        {poll?.exists && poll.options?.length > 0 && (
          <div className="rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-800 p-3">
            <div className="text-sm font-semibold text-gray-800 dark:text-white">
              {poll.question || '(chưa có câu hỏi)'}
            </div>
            {poll.options.map((o) => (
              <div key={o.id} className="mt-2">
                <div className="flex justify-between text-xs text-gray-600 dark:text-gray-300">
                  <span className="truncate">
                    {o.label}
                    {o.isLeading && ' 🏆'}
                  </span>
                  <span>
                    {formatCa(o.totalAmount)} Ca · {o.percent}%
                  </span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700">
                  <div className="h-full rounded-full bg-blue-600" style={{ width: `${o.percent}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}

        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="mt-[3px] h-4 w-4 shrink-0 rounded border-[#C8CCD8] accent-blue-600"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Mở bình chọn
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              Tắt thì người xem không bình chọn được và overlay không hiện gì.
            </span>
          </span>
        </label>

        <div className="flex flex-col gap-1">
          <label htmlFor="poll-q" className="text-sm">
            Câu hỏi
          </label>
          <input
            id="poll-q"
            value={question}
            maxLength={120}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="VD: Tối nay chơi game gì?"
            className={`${inputCls} w-full`}
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">Phương án (tối đa {MAX_OPTIONS})</span>
          {options.map((o, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                value={o}
                maxLength={60}
                onChange={(e) =>
                  setOptions((p) => p.map((v, i) => (i === idx ? e.target.value : v)))
                }
                placeholder={`Phương án ${idx + 1}`}
                className={`${inputCls} min-w-0 flex-1`}
              />
              <button
                type="button"
                onClick={() => setOptions((p) => p.filter((_, i) => i !== idx))}
                disabled={options.length <= 2}
                title="Xoá phương án"
                className="shrink-0 rounded-lg border border-[#C8CCD8] dark:border-slate-600 p-2 text-red-500 disabled:opacity-40"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {options.length < MAX_OPTIONS && (
            <button
              type="button"
              onClick={() => setOptions((p) => [...p, ''])}
              className="flex w-fit items-center gap-1 rounded-lg border border-[#C8CCD8] dark:border-slate-600 px-3 py-2 text-sm dark:text-white"
            >
              <Plus size={16} />
              Thêm phương án
            </button>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="poll-dur" className="text-sm">
            Thời lượng (phút)
          </label>
          <input
            id="poll-dur"
            type="number"
            min={1}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            className={`${inputCls} w-full`}
          />
        </div>

        {poll?.exists && (
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={startNewRound}
              onChange={(e) => setStartNewRound(e.target.checked)}
              className="mt-[3px] h-4 w-4 shrink-0 rounded border-[#C8CCD8] accent-blue-600"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Mở đợt mới
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Đếm lại từ 0 và tính giờ lại từ lúc lưu. Phiếu của đợt trước không còn
                được tính. Không tick thì phiếu đã có vẫn giữ nguyên, giờ kết thúc cũng
                không đổi. Muốn bỏ một phương án đã có người bình chọn thì phải tick ô này.
              </span>
            </span>
          </label>
        )}

        <div className="flex flex-col gap-1">
          <h2 className="text-sm">
            Link hiển thị bình chọn ( Add Webpage URL trong phần mềm Stream )
          </h2>
          <div className="flex items-center gap-2">
            <div className="flex h-12 min-w-0 flex-1 items-center rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-[#EBECF2] dark:bg-slate-700 px-3 text-base text-[#A1A5B6] dark:text-slate-400">
              <span className="w-full truncate">{overlayUrl}</span>
            </div>
            <button
              type="button"
              onClick={copyOverlay}
              title="Sao chép link"
              className="flex h-12 shrink-0 items-center gap-1 rounded-lg bg-[#39539E] px-4 text-white"
            >
              <Copy size={16} />
              Sao chép
            </button>
          </div>
        </div>

        <div className="flex justify-between gap-3">
          <button
            type="button"
            onClick={() => window.open(overlayUrl, 'popup', 'width=700,height=520,top=100,left=100')}
            className="rounded-lg bg-[#39539E] px-4 py-2 text-base text-white"
          >
            Xem thử
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-blue-500 px-4 py-2 text-base text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Đang lưu…' : 'Lưu bình chọn'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DonatePollSetting;
